import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'
import { ROLE_LABELS, UpdateProfileSchema, phoneSchema } from '@tumin/domain'
import { ControlledField, FormServerError } from '@/components/form-field'
import { KeyValueList } from '@/components/key-value-list'
import { OptionGroup } from '@/components/option-group'
import { PageHeader } from '@/components/page-header'
import { SectionCard } from '@/components/section-card'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ChangePasswordForm } from '@/features/auth/change-password-form'
import { PushOptIn } from '@/features/profile/push-opt-in'
import { SessionsCard } from '@/features/profile/sessions-card'
import { TwoFactorCard } from '@/features/profile/two-factor-card'
import { api } from '@/lib/api'
import { employeeInitials, useAuthStore, type DensityPreference, type SessionEmployee, type ThemePreference } from '@/lib/auth'
import { applyApiErrors, useZodForm } from '@/lib/form'
import { notifySuccess } from '@/lib/notify'
import { DENSITY_LABELS, THEME_LABELS, usePreferencesStore, useSavePreferences } from '@/lib/preferences'
import { ROUTES } from '@/lib/routes'
import { endSession } from '@/lib/session'

export const Route = createFileRoute('/_app/profile')({
  component: ProfilePage,
})

/** Datos personales editables; el teléfono puede quedar vacío. */
const profileFormSchema = z.object({
  fullName: UpdateProfileSchema.shape.fullName.unwrap(),
  phone: z.union([z.literal(''), phoneSchema]),
})

const THEME_OPTIONS = (Object.keys(THEME_LABELS) as ThemePreference[]).map((value) => ({ value, label: THEME_LABELS[value] }))
const DENSITY_OPTIONS = (Object.keys(DENSITY_LABELS) as DensityPreference[]).map((value) => ({
  value,
  label: DENSITY_LABELS[value],
  description: value === 'comfortable' ? 'Controles grandes, ideal para teléfono' : 'Más información por pantalla',
}))

function ProfilePage() {
  const employee = useAuthStore((s) => s.employee)
  const router = useRouter()
  const theme = usePreferencesStore((s) => s.theme)
  const density = usePreferencesStore((s) => s.density)
  const savePreferences = useSavePreferences()

  const form = useZodForm(profileFormSchema, {
    defaultValues: { fullName: employee?.fullName ?? '', phone: employee?.phone ?? '' },
  })
  const saveProfile = useMutation({
    mutationFn: (values: z.output<typeof profileFormSchema>) =>
      api.patch<SessionEmployee>('/employees/me', { fullName: values.fullName, ...(values.phone ? { phone: values.phone } : {}) }),
    onSuccess: (updated) => {
      useAuthStore.getState().setEmployee(updated)
      form.reset({ fullName: updated.fullName, phone: updated.phone ?? '' })
      notifySuccess('Datos actualizados')
    },
    onError: (error) => applyApiErrors(form, error),
  })

  const onPasswordChanged = async () => {
    await endSession()
    notifySuccess('Contraseña actualizada', 'Por seguridad se cerraron todas sus sesiones. Ingrese con la nueva contraseña.')
    await router.navigate({ to: ROUTES.login })
  }

  if (!employee) return null

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <span aria-hidden className="flex size-14 shrink-0 items-center justify-center rounded-[18px] bg-highlight font-heading text-[1.375rem] font-extrabold text-highlight-foreground sm:size-16 sm:text-2xl">
            {employeeInitials(employee)}
          </span>
          <PageHeader eyebrow={`Cuenta · ${ROLE_LABELS[employee.role]}`} title="Mi perfil" description="Sus datos, preferencias y seguridad de acceso." />
        </div>
        {employee.twoFactorEnabled && <StatusBadge label="Cuenta protegida con dos pasos" tone="success" className="self-start sm:self-auto" />}
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <SectionCard
          title="Datos personales"
          footer={
            <Button type="submit" form="profile-form" disabled={saveProfile.isPending || !form.formState.isDirty}>
              {saveProfile.isPending && <Loader2 className="animate-spin" />} Guardar
            </Button>
          }
        >
          <form id="profile-form" noValidate onSubmit={form.handleSubmit((values) => saveProfile.mutate(values))} className="space-y-4">
            <FormServerError errors={form.formState.errors} />
            <ControlledField control={form.control} name="fullName" label="Nombre completo" render={({ field, inputProps }) => <Input {...inputProps} {...field} autoComplete="name" />} />
            <ControlledField
              control={form.control}
              name="phone"
              label="Teléfono"
              hint="8 dígitos, sin guiones."
              render={({ field, inputProps }) => <Input {...inputProps} {...field} type="tel" inputMode="tel" autoComplete="tel" />}
            />
            <KeyValueList
              items={[
                { label: 'Correo', value: employee.email },
                { label: 'Rol', value: ROLE_LABELS[employee.role] },
              ]}
            />
          </form>
        </SectionCard>

        <SectionCard title="Apariencia" description="Se guarda en su perfil y aplica en todos sus dispositivos.">
          <div className="space-y-5">
            <div className="space-y-2">
              <p className="text-sm font-medium">Tema</p>
              <OptionGroup label="Tema" options={THEME_OPTIONS} value={theme} onChange={(value) => savePreferences.mutate({ theme: value })} />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Densidad</p>
              <OptionGroup label="Densidad" columns={2} options={DENSITY_OPTIONS} value={density} onChange={(value) => savePreferences.mutate({ density: value })} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Cambiar contraseña" description="Al cambiarla se cierran las sesiones en todos sus dispositivos.">
          <ChangePasswordForm onChanged={onPasswordChanged} />
        </SectionCard>

        <div className="space-y-4">
          <TwoFactorCard />
          <PushOptIn />
          <SessionsCard />
        </div>
      </div>
    </div>
  )
}
